/**
 * CookieAuth - JWT token validation helpers
 */

import { getAuthCookie } from './coreCookies'

/** Decode JWT payload without verification */
function decodeJWT(token: string): Record<string, unknown> | null {
  try {
    if (!token || typeof token !== 'string') return null
    const parts = token.split('.')
    if (parts.length !== 3) return null
    const decoded = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))
    return JSON.parse(decoded)
  } catch {
    return null
  }
}

/** Validate JWT structure: 3 parts, valid base64 header/payload, has exp */
export function isValidJWT(token: string): boolean {
  if (!token || typeof token !== 'string') return false
  const parts = token.split('.')
  if (parts.length !== 3) return false
  try {
    const header = JSON.parse(atob(parts[0].replace(/-/g, '+').replace(/_/g, '/')))
    if (!header.alg || !header.typ) return false
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')))
    if (typeof payload.exp !== 'number') return false
    if (!parts[2] || parts[2].length === 0) return false
    return true
  } catch {
    return false
  }
}

/** Get decoded JWT payload */
export function getJWTPayload(token: string): Record<string, unknown> | null {
  return decodeJWT(token)
}

/** Check if a JWT is expired with optional buffer in seconds */
export function isTokenExpired(token: string, bufferSeconds = 30): boolean {
  const payload = decodeJWT(token)
  if (!payload || typeof payload.exp !== 'number') return true
  return Date.now() >= (payload.exp * 1000) - (bufferSeconds * 1000)
}

/** Get token expiration timestamp in milliseconds */
export function getTokenExpiry(token: string): number | null {
  const payload = decodeJWT(token)
  if (!payload || typeof payload.exp !== 'number') return null
  return payload.exp * 1000
}

/** Check if cookie auth is valid (token exists and not expired) */
export function isCookieAuthValid(): boolean {
  const token = getAuthCookie()
  if (!token) return false
  return !isTokenExpired(token)
}

/** Check if session needs refresh soon (within 5 minutes of expiry) */
export function needsTokenRefresh(): boolean {
  const token = getAuthCookie()
  if (!token) return false
  return isTokenExpired(token, 5 * 60)
}
